import React, { useCallback, useMemo, useState } from 'react';
import { mobileConfig } from '../../commons/config';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import useApi from '../../api/backendApiContext';
import { ConfirmDialogProps, ConfirmModal } from '../../commons/components/ConfirmModal';
import { useNotification } from '../../notification/notificationsContext';
import { errorToString } from '../../util/utils';
import youTubePermissionsImage from '../../assets/images/youtube-permissions.png';
import { BASE_URL } from '../../commons/utils';
import { IntegrationTile } from '../../commons/components/IntegrationTile';
import { ExternalBrowserDialog } from '../../commons/components/ExternalBrowserDialog';
import { useFetchedYouTubeAccount } from '../hooks/useFetchedYouTubeAccount';
import { notConnectedYouTubeAccount } from '../../social-posting/socialPostingApi';
import youtubeLogo from '../../assets/images/youtube.png';

const requiredGoogleScopes = [
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/youtube.readonly',
  'https://www.googleapis.com/auth/youtube.upload',
];

export const YouTubeSettings = () => {
  const {
    query: { isLoading, isError, data },
    queryStatusComponent,
  } = useFetchedYouTubeAccount();
  const { socialPostingApi } = useApi();
  const { notifySuccess, notifyError } = useNotification();

  const isActive = !!data && !data.isDisconnected;
  const isDisconnected = !!data && data.isDisconnected;

  const queryCacheManager = useQueryCacheManager();

  const { isLoading: isConnecting, mutate: connect } = useMutation(
    async (authCode: string) => socialPostingApi.authenticateYouTube(authCode, BASE_URL),
    {
      onSuccess: (youTubeAccount) => {
        queryCacheManager.setYouTubeAccount(youTubeAccount);
        const message = 'Connection with YouTube has been successfully setup';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(AnalyticsEvent.YOUTUBE_CONNECTED);
      },
      onError: (e) => {
        const message = errorToString(
          e,
          'Something when wrong while setting up connection with YouTube. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const login = useGoogleLogin({
    onSuccess: (codeResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => {
      const actualScopes = codeResponse.scope.split(' ');
      const missingScopes = requiredGoogleScopes.filter((x) => !actualScopes.includes(x));
      if (missingScopes.length > 0) {
        analyticsTrack(AnalyticsEvent.YOUTUBE_MISSING_PERMISSIONS);
        setConfirmReconnectModal(true);
        return;
      }
      connect(codeResponse.code);
    },
    flow: 'auth-code',
    scope: requiredGoogleScopes.join(' '),
    overrideScope: true,
    ux_mode: 'popup',
  });

  const { isLoading: isDisconnecting, mutate: disconnect } = useMutation(
    async () => socialPostingApi.disconnectYouTube(),
    {
      onSuccess: () => {
        queryCacheManager.setYouTubeAccount(notConnectedYouTubeAccount);
        const message = 'Connection with YouTube has been successfully stopped.';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(AnalyticsEvent.YOUTUBE_DISCONNECTED);
      },
      onError: (e) => {
        const message = errorToString(
          e,
          'Something when wrong while disconnecting from YouTube. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const [confirmDisconnectModal, setConfirmDisconnectModal] = useState(false);
  const [confirmReconnectModal, setConfirmReconnectModal] = useState(false);

  const onDisconnectConfirmed = useCallback(async () => {
    setConfirmDisconnectModal(false);
    await disconnect();
  }, [disconnect]);

  const disconnectConfirmDialogProps: ConfirmDialogProps = useMemo<ConfirmDialogProps>(
    () => ({
      title: 'Disconnect Growegy from YouTube?',
      message: 'You’ll be able to reconnect at any time.',
      footer: {
        type: 'show',
        applyButton: {
          label: 'Disconnect',
          variant: 'danger',
          onClick: onDisconnectConfirmed,
        },
        cancelButton: {
          label: 'Cancel',
          onClick: () => setConfirmDisconnectModal(false),
        },
      },
    }),
    [onDisconnectConfirmed]
  );

  const reconnectConfirmDialogProps: ConfirmDialogProps = useMemo<ConfirmDialogProps>(
    () => ({
      title: 'Growegy needs more permissions to connect to YouTube',
      message:
        'YouTube could not be connected because not all the requested permissions have been granted. ' +
        'Please start over and grant all the requested permissions.',
      footer: {
        type: 'show',
        applyButton: {
          label: 'Connect',
          variant: 'primary',
          onClick: () => {
            setConfirmReconnectModal(false);
            login();
          },
        },
        cancelButton: {
          label: 'Cancel',
          onClick: () => setConfirmReconnectModal(false),
        },
      },
    }),
    [login]
  );
  const [openBrowserKey, setOpenBrowserKey] = useState<string | null>(null);

  const onConnectClick = useCallback(() => {
    if (mobileConfig().isMobile) {
      setOpenBrowserKey(new Date().toString());
      return;
    }
    login();
  }, [login]);

  return (
    <>
      <IntegrationTile
        isActive={isActive}
        isDisconnected={isDisconnected}
        isConnecting={isConnecting}
        isDisconnecting={isDisconnecting}
        isSchedulingResync={false}
        isLoading={isLoading}
        isError={isError}
        queryStatusComponent={queryStatusComponent}
        title="YouTube"
        subtitle={data ? data.name : null}
        privacyPolicyUrl="https://www.growegy.com/resources/about/privacy-policy/"
        onConnectClick={onConnectClick}
        onDisconnectClick={() => setConfirmDisconnectModal(true)}
        icon={
          <img
            src={youtubeLogo}
            className="integration-row-icon"
            alt="YouTube"
            style={{ marginLeft: -16 }}
          />
        }
      >
        <div>
          Connect your YouTube account directly to create and schedule videos, shorts and posts.
        </div>
      </IntegrationTile>
      {confirmDisconnectModal && <ConfirmModal {...disconnectConfirmDialogProps} />}
      {confirmReconnectModal && (
        <ConfirmModal {...reconnectConfirmDialogProps}>
          <div className="mt-5 w-100">
            <img
              src={youTubePermissionsImage}
              alt="Grant all the requested YouTube permissions"
              width="400px"
              style={{ maxWidth: '100%' }}
              height="auto"
              className="ml-auto mr-auto d-block"
            />
          </div>
        </ConfirmModal>
      )}
      <ExternalBrowserDialog dialogKey={openBrowserKey} title="Connect YouTube">
        To integrate your YouTube account with Growegy, you'll be redirected to an external browser
        to securely log in and complete the connection. Tap Proceed to continue.
      </ExternalBrowserDialog>
    </>
  );
};
